<template>
  <long-query-layout
    :resource-name="resourceName"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :result-fetch-url.sync="resultTable.url"
    :columns.sync="resultTable.columns"
    :group-headers.sync="resultTable.groupHeaders"
    :active-tab.sync="activeTab"
    :excel-properties-handler="excelPropertiesHandler.bind(this)"
    :convert-menu-default-query-filter="convertMenuDefaultQueryFilter"
    @result-area-size-change="updateTableSize"
    @query-completed="onQueryCompleted"
    @query-id-change="queryId = $event"
    ref="parentLayout"
  >
    <!-- -------------------------------------------------------- -->
    <!-- ヘッダのアクション -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:header-actions="slotProps">
      <gl-switch
        :value="isDetail"
        :disabled="!(slotProps.queryId && activeTab == 'result')"
        @input="toggleDetail"
      >
        期間展開
      </gl-switch>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term" title="期間" contentWidth="medium">
        <filter-term-pos
          v-bind.sync="queryFilter.term"
          :default-week-count="8"
        />
      </query-filter-element>

      <!-- 商品分類 -->
      <query-filter-element id="category-item-category" title="商品分類">
        <gl-select-company :company.sync="queryFilter.item.company" />
        <filter-category-item-category
          :company="queryFilter.item.company"
          v-bind.sync="queryFilter.item"
          :pos-restriction="true"
          :use-new-item-component="true"
        />
      </query-filter-element>

      <!-- 店舗 -->
      <query-filter-element id="store" title="店舗">
        <filter-store v-bind.sync="queryFilter.store" />
      </query-filter-element>

      <!-- オプション -->
      <query-filter-element id="option-pos" title="オプション">
        <filter-option-pos v-bind.sync="queryFilter.option" />
      </query-filter-element>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <div
        :gl-tooltip="
          !$store.state.isQueryFullFeatured
            ? msg.MSG_QUERY_FULL_FEATURE_UNAVAILABLE
            : ''
        "
        gl-tooltip-direction="left"
        class="button-group button-group__layout"
      >
        <md-button
          v-for="level in itemCategoryLevels"
          class="md-raised button-group__button"
          :value="level.level"
          :key="level.level"
          :disabled="!$store.state.isQueryFullFeatured"
          @click="drilldown(level.level)"
          >{{ level.name }}へ
        </md-button>
      </div>

      <pos-transition-dialog
        :open.sync="transitionOpen"
        :trigger="transitionTriggerEvent"
        :title="transitionData.title"
        :destinations="destinations"
        @click="transition"
      ></pos-transition-dialog>

      <gl-table ref="myTable" v-bind="resultTable" />

      <md-snackbar
        md-position="center"
        :md-duration="4000"
        :md-active.sync="showSnackbar"
        md-persistent
      >
        <span>対象の分類を一覧から選択してください</span>
      </md-snackbar>
    </template>
  </long-query-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTermPos from '@/components/FilterTermPos.vue'
import FilterCategoryItemCategory from '@/components/FilterCategoryItemCategory.vue'
import FilterStore from '@/components/FilterStore.vue'
import FilterOptionPos from '@/components/FilterOptionPos.vue'
// 分析条件モデル
import termPos from '@/queryfilter/termPos'
import categoryItemCategory from '@/queryfilter/categoryItemCategory2'
import store from '@/queryfilter/store'
// 分析結果
import GlTable from '@/components/GlTable.vue'

import PosTransitionDialog from '@/components/PosTransitionDialog.vue'
import {
  standardTableCustomFunction,
  standardWithSearchCustomFunction,
} from '@/util/custom_formatters'
import msg from '@/const/messages'

export default {
  components: {
    QueryFilterElement,
    FilterTermPos,
    FilterCategoryItemCategory,
    FilterStore,
    FilterOptionPos,
    LongQueryLayout,
    GlTable,
    PosTransitionDialog,
  },
  data() {
    return {
      msg,
      resourceName: 'chocostick-i',
      bottomItemCategoryLevel: undefined,
      showSnackbar: false,
      destinations: [
        {
          resourceName: 'chocostick-s',
          label: '店舗xKPI',
        },
        {
          resourceName: 'chocostick-t',
          label: '期間xKPI (日別)',
        },
        {
          resourceName: 'chocostick-m',
          label: 'メーカーxKPI',
        },
      ],
      /**
       * ドリルダウン先の階層
       */
      itemCategoryLevels: [],

      queryId: undefined,
      /**
       * 表示タブ（分析条件、分析結果）
       *
       * 表示タブをプログラム側から切り替えたい場合をこれを使います。
       */
      activeTab: undefined,
      /**
       * 分析条件
       */
      queryFilter: {
        term: {
          order: 1,
          summaryKey: 'termPos',
          ...termPos.get(),
          visibleTermTypes: ['d', 'w', 'm', 'closing'],
        },
        item: {
          order: 2,
          summaryKey: 'categoryItemCategory',
          enableFilterNewItems: true,
          ...categoryItemCategory.get(),
        },
        store: { order: 3, ...store.get() },
        option: {
          order: 4,
          summaryKey: 'optionPos',
          evaluations: [
            'amount',
            'qty',
            'amount_per_qty',
            'qty_pi',
            'amount_pi',
          ],
          errors: [],
        },
        drilldown: {
          queryId: undefined,
        },
      },
      /**
       * 分析結果
       */
      resultTable: {
        columns: undefined,
        groupHeaders: undefined,
        sortColumnName: 'code',
        sortOrder: 'asc',
        selectable: true,
        url: undefined,
        width: 0,
        height: 0,
        customFormatter: {
          standard: standardTableCustomFunction,
          linkifySearch: standardWithSearchCustomFunction,
          linkify: (rowData, cell) => {
            cell.classList.add('link-cell')
            cell.addEventListener('click', e => {
              this.transitionData.title =
                rowData.date === '合計'
                  ? rowData.name
                  : rowData.date + ' | ' + rowData.name
              this.transitionData.base.dateFrom = rowData.date_from
              this.transitionData.base.dateTo = rowData.date_to
              this.transitionData.compare.dateFrom = rowData.compare_date_from
              this.transitionData.compare.dateTo = rowData.compare_date_to
              this.transitionData.code = rowData.code
              this.transitionTriggerEvent = e
              this.transitionOpen = true
            })
          },
        },
      },
      /**
       * 詳細表示
       */
      isDetail: false,
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: queryFilter => ({
        term: termPos.mapToApiPayload(queryFilter.term),
        category:
          queryFilter.item.selectMode === 'category'
            ? categoryItemCategory.mapToApiPayload(queryFilter.item)
            : null,
        item:
          queryFilter.item.selectMode === 'category'
            ? null
            : categoryItemCategory.mapToApiPayload(queryFilter.item),
        store: store.mapToApiPayload(queryFilter.store),
        option: {
          evaluations: queryFilter.option.evaluations,
        },
        drilldown: queryFilter.drilldown,
      }),
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       */
      applyQueryFilter: queryFilter => {
        termPos.applyPayload(this.queryFilter.term, queryFilter.term)
        categoryItemCategory.applyPayload(
          this.queryFilter.item,
          queryFilter.item,
        )
        store.applyPayload(this.queryFilter.store, queryFilter.store)
        this.queryFilter.option.evaluations = queryFilter.option.evaluations
        if (queryFilter.drilldown) {
          this.queryFilter.drilldown.queryId = undefined
        }
      },
      /**
       * Excel出力のパラメータ生成
       */
      excelPropertiesHandler() {
        const { name, order } = this.$refs.myTable.getCurrentSort()
        return {
          sortColumnName: name,
          sortOrder: order,
          showDetail: this.isDetail,
        }
      },
      // 画面遷移ダイアログの開閉状態
      transitionOpen: false,
      transitionTriggerEvent: undefined,
      transitionData: {
        title: undefined,
        base: {
          dateFrom: undefined,
          dateTo: undefined,
        },
        compare: {
          dateFrom: undefined,
          dateTo: undefined,
        },
        code: undefined,
      },
    }
  },
  watch: {
    'queryFilter.item.selectMode'(newVal) {
      this.resultTable.selectable = newVal === 'category' && !this.isDetail
    },
    isDetail(newVal) {
      this.resultTable.selectable =
        this.queryFilter.item.selectMode === 'category' && !newVal
    },
  },
  methods: {
    /**
     * 結果表示テーブルのサイズを更新する
     */
    updateTableSize(size) {
      this.resultTable.width = size.width
      this.resultTable.height = size.height - 50
    },
    toggleDetail(value) {
      this.isDetail = value

      if (this.$refs.myTable.c3table) {
        // 期間展開をON/OFFした際にソート順をキープします
        // キープはできるのですが、テーブルの表示上は▲▼アイコンは
        // 出ません（テーブルライブラリの仕様上…）。
        const { name, order } = this.$refs.myTable.getCurrentSort()
        this.resultTable.sortColumnName = name
        this.resultTable.sortOrder = order
      }

      if (this.isDetail) {
        this.resultTable.url += '?showDetail=true'
      } else {
        if (
          this.resultTable.url &&
          this.resultTable.url.lastIndexOf('?showDetail=true') > -1
        ) {
          this.resultTable.url = this.resultTable.url.substring(
            0,
            this.resultTable.url.lastIndexOf('?showDetail=true'),
          )
        }
      }
    },
    onQueryCompleted() {
      this.toggleDetail(this.isDetail)
      this.loadItemCategoryLevelsData()
    },
    /**
     * 選択しているカテゴリ階層以下＋商品をロード
     */
    loadItemCategoryLevelsData() {
      if (this.queryFilter.item.selectMode === 'category') {
        const itemCategoryLevels = this.$store.state.itemCategoryLevels[
          this.queryFilter.item.company
        ]

        //商品のために最下層を取得しておく
        this.bottomItemCategoryLevel =
          itemCategoryLevels[itemCategoryLevels.length - 1].level

        //条件で選択している階層までは削除
        const deleteIndex = itemCategoryLevels.findIndex(
          i => i.level == this.queryFilter.item.category.itemCategoryLevel,
        )

        this.itemCategoryLevels = [
          ...itemCategoryLevels.slice(deleteIndex + 1),
          {
            level: null,
            name: '商品',
          },
        ]
      } else {
        this.itemCategoryLevels = []
      }
    },
    /**
     * ドリルダウン処理
     **/
    async drilldown(itemCategoryLevel) {
      const selectedRows = this.$refs.myTable.getSelectedRowData()
      const allChecked = this.$refs.myTable.isSelectAllChecked()
      if (selectedRows.length === 0 && !allChecked) {
        this.showSnackbar = true
        return
      }
      this.showSnackbar = false
      this.queryFilter.drilldown.queryId = this.queryId
      const fromItemCategoryLevel = this.queryFilter.item.category
        .itemCategoryLevel
      const toItemCategoryLevel =
        itemCategoryLevel !== null
          ? itemCategoryLevel
          : this.bottomItemCategoryLevel
      let categories = selectedRows.map(r => r.code)

      // 全選択チェックボックス入っている場合はselectedRows関係なく全行取得してドリルダウンを行う。
      if (allChecked) {
        // 全データのカテゴリコード取得
        const { name, order } = this.$refs.myTable.getCurrentSort()
        // 取得行数を99999にして、全件取得する。
        const params = {
          numberOfRows: 99999,
          currentPage: 1,
          sortColumnName: name,
          sortOrder: order,
        }
        categories = await this.$longQuery.fetchAllCategoryListForDrillDown(
          this.resourceName,
          this.queryId,
          params,
        )
      }
      const drilldownCategories = await this.getTargetDrilldownCategories(
        this.queryFilter.item.company,
        fromItemCategoryLevel,
        toItemCategoryLevel,
        categories,
      )
      if (itemCategoryLevel == null) {
        //商品へは商品(分類)へ切り替え
        this.queryFilter.item.selectMode = 'itemCategory'
        this.queryFilter.item.item.filterNewItems = this.queryFilter.item.category.filterNewItems
        this.queryFilter.item.item.categories = drilldownCategories
      } else {
        //各カテゴリへ
        this.queryFilter.item.category.itemCategoryLevel = itemCategoryLevel
        this.queryFilter.item.category.categories = drilldownCategories
      }

      this.$refs.parentLayout.executeQuery()
    },
    /**
     * fromのcategoriesよりtoのカテゴリを取得
     */
    async getTargetDrilldownCategories(company, from, to, categories) {
      const chunkSize = 100
      const targetDrilldownCategories = []
      for (let i = 0; i < categories.length; i += chunkSize) {
        const chunkCategories = categories.slice(i, i + chunkSize)
        const chunkDrilldownCategories = await this.$repositories
          .get('itemCategory')
          .getDrilldownData(company, from, to, chunkCategories)
        targetDrilldownCategories.push(...chunkDrilldownCategories)
      }
      return targetDrilldownCategories.map(c => c.code)
    },
    // デフォルトQueryFilterの変換処理
    // 商品グループは一時的なものなのでメニューのデフォルト条件
    // としては保持しない。
    convertMenuDefaultQueryFilter(queryFilter) {
      queryFilter.item.item.itemGroupId = undefined
      return queryFilter
    },
    async transition(destination) {
      const override = termPos.getOverride(
        this.transitionData,
        destination,
        this.queryFilter,
      )

      if (this.transitionData.code) {
        if (this.queryFilter.item.selectMode === 'category') {
          override['item'] = {
            selectMode: 'category',
            category: {
              categories: [this.transitionData.code],
            },
          }
        } else {
          override['item'] = {
            selectMode: 'item',
            // 商品は単に商品コード一覧ではなく、商品データ（←名称とか分類とか含む）
            // を設定する必要があるのでAPIから取得します。
            // ここでは必ず1商品のはずなのでURLが長くなりすぎる問題は考慮しません。
            // TODO 商品選択コンポーネントが商品コードベースに対応していればこんなことせずにすむ…
            items: await this.$repositories
              .get('item')
              .getByCode(
                this.queryFilter.item.company,
                [this.transitionData.code],
                {
                  categoryLevel: undefined,
                  categories: undefined,
                },
              ),
            categories: [],
            // 削除しておかないと遷移先の画面で正しく商品条件が復元できない…
            // TODO 分析条件周りの処理が非常に複雑になってしまった…
            item: null,
            category: null,
          }
        }
      }

      const url = `${process.env.BASE_URL}query-${destination}?reference=${
        this.queryId
      }&override=${JSON.stringify(override)}`
      window.open(url)
    },
  },
}
</script>
